import { useState, useEffect } from 'react';
import { FiX, FiInbox } from 'react-icons/fi';
import { getAllLedgerAccounts } from '../../services/api';
import { today } from '../../utils/dateUtils';
import rStyles from '../Receipts/NewReceiptsPage.module.css';
import styles from './OfferingsPage.module.css';

import { fmtCurrency } from '../../utils/formatNumber';
const PAYMENT_MODES = [
  { value: 'cash',          label: 'Cash' },
  { value: 'upi',           label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque',        label: 'Cheque' },
];

/**
 * Modal that appears after "Create Offering + Receipt" to collect receipt-specific fields.
 *
 * Props:
 *   data       – { memberName, offerings: [{ id, amount, categoryName }], totalAmount, derasarId, financialYearId }
 *   saving     – bool
 *   onConfirm  – fn({ receiptDate, debitLedgerAccountId, paymentMode, chequeNo, narration })
 *   onClose    – fn()
 */
export default function OfferingsReceiptModal({ data, saving, onConfirm, onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [receiptDate,       setReceiptDate]  = useState(() => today());
  const [debitLedgerAcctId, setDebitAcctId]  = useState('');
  const [paymentMode,       setPaymentMode]  = useState('cash');
  const [referenceNo,       setReferenceNo]  = useState('');
  const [narration,         setNarration]    = useState('');

  // Load asset (cash/bank) accounts when modal opens
  useEffect(() => {
    if (!data?.derasarId) return;
    getAllLedgerAccounts(data.derasarId)
      .then((res) => {
        const assetAccounts = (res.data || []).filter(
          (a) => a.isActive && ['cash', 'bank'].includes(a.accountType)
        );
        setAccounts(assetAccounts);
        // Auto-select first cash account and default to cash mode
        const cashAcct = assetAccounts.find((a) => a.accountType === 'cash');
        if (cashAcct) {
          setDebitAcctId(String(cashAcct.id));
          setPaymentMode('cash');
        }
      })
      .catch(() => {});
  }, [data?.derasarId]);

  // When payment mode changes, sync the debit account to match
  const handlePaymentModeChange = (mode) => {
    setPaymentMode(mode);
    if (mode === 'cash') {
      // Switch to first cash account
      const cashAcct = accounts.find((a) => a.accountType === 'cash');
      if (cashAcct) setDebitAcctId(String(cashAcct.id));
    } else {
      // UPI / bank_transfer / cheque ? switch to first bank account
      const currentAcct = accounts.find((a) => String(a.id) === debitLedgerAcctId);
      if (currentAcct?.accountType !== 'bank') {
        const bankAcct = accounts.find((a) => a.accountType === 'bank');
        if (bankAcct) setDebitAcctId(String(bankAcct.id));
      }
    }
  };

  // When debit account changes, infer the payment mode
  const handleDebitAcctChange = (id) => {
    setDebitAcctId(id);
    const acct = accounts.find((a) => String(a.id) === id);
    if (!acct) return;
    if (acct.accountType === 'cash') {
      setPaymentMode('cash');
    } else if (acct.accountType === 'bank') {
      // Keep existing bank mode (upi/bank_transfer/cheque); only switch if currently on cash
      if (paymentMode === 'cash') setPaymentMode('bank_transfer');
    }
  };

  if (!data) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!debitLedgerAcctId) return;
    onConfirm({
      receiptDate,
      debitLedgerAccountId: debitLedgerAcctId,
      paymentMode,
      referenceNo: ['cheque', 'upi', 'bank_transfer'].includes(paymentMode) ? referenceNo || undefined : undefined,
      narration: narration || undefined,
    });
  };

  return (
    <div className={styles.receiptModalOverlay} onClick={onClose}>
      <div className={styles.receiptModal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.receiptModalHeader}>
          <div className={styles.receiptModalTitle}>
            <FiInbox size={18} />
            Create Receipt
          </div>
          <button className={styles.receiptModalClose} onClick={onClose} disabled={saving}>
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.receiptModalBody}>

            {/* Locked info */}
            <div className={styles.receiptModalInfo}>
              <div className={styles.receiptModalInfoRow}>
                <span className={styles.receiptModalInfoLabel}>Member</span>
                <span className={styles.receiptModalInfoValue}>{data.memberName}</span>
              </div>
              <div className={styles.receiptModalInfoRow}>
                <span className={styles.receiptModalInfoLabel}>
                  {data.offerings.length === 1 ? 'Offering' : 'Offerings'}
                </span>
                <span className={styles.receiptModalInfoValue}>
                  {data.offerings.length === 1
                    ? `${data.offerings[0].categoryName} — ${fmtCurrency(data.offerings[0].amount)}`
                    : `${data.offerings.length} offerings â â¹${fmtCurrency(data.totalAmount)}`}
                </span>
              </div>
              <div className={styles.receiptModalInfoRow}>
                <span className={styles.receiptModalInfoLabel}>Total</span>
                <span className={`${styles.receiptModalInfoValue} ${styles.receiptModalTotal}`}>
                  {fmtCurrency(data.totalAmount)}
                </span>
              </div>
            </div>

            {/* Receipt Date */}
            <div className="form-group">
              <label>Receipt Date *</label>
              <input
                type="date"
                value={receiptDate}
                required
                onChange={(e) => setReceiptDate(e.target.value)}
              />
            </div>

            {/* Debit Account */}
            <div className="form-group">
              <label>Debit Account (Cash / Bank) *</label>
              <select
                value={debitLedgerAcctId}
                required
                onChange={(e) => handleDebitAcctChange(e.target.value)}
              >
                <option value="">— Select account —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} [{a.accountType?.toUpperCase()}]{a.accountCode ? ` · ${a.accountCode}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Mode */}
            <div className="form-group">
              <label>Payment Mode *</label>
              <div className={rStyles.modeRow}>
                {PAYMENT_MODES.map((m) => (
                  <label
                    key={m.value}
                    className={`${rStyles.modeChip} ${paymentMode === m.value ? rStyles.modeChipOn : ''}`}
                  >
                    <input
                      type="radio"
                      name="rm-paymentMode"
                      value={m.value}
                      checked={paymentMode === m.value}
                      onChange={() => handlePaymentModeChange(m.value)}
                    />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>

            {['cheque', 'upi', 'bank_transfer'].includes(paymentMode) && (
              <div className="form-group">
                <label>
                  {paymentMode === 'cheque' ? 'Cheque Number' : paymentMode === 'upi' ? 'UPI Reference No' : 'Bank Reference No'}
                  <em style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.85em' }}> (optional)</em>
                </label>
                <input
                  value={referenceNo}
                  placeholder={paymentMode === 'cheque' ? 'e.g. 001234' : 'e.g. TXN123456'}
                  onChange={(e) => setReferenceNo(e.target.value)}
                />
              </div>
            )}

            {/* Narration */}
            <div className="form-group">
              <label>Narration <em style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.85em' }}>(optional)</em></label>
              <input
                value={narration}
                placeholder="Optional note or reference"
                onChange={(e) => setNarration(e.target.value)}
              />
            </div>

          </div>

          {/* Footer */}
          <div className={styles.receiptModalFooter}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Skip
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || !debitLedgerAcctId}>
              {saving ? 'Creating…' : 'Create Receipt'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
